/**
 * size-recommender.js
 * Custom element for snippets/size-recommender.liquid ("Find My Size").
 *
 * Registers <size-recommender>. Reads its size table from data-sizes (written by the Liquid,
 * already paired against the product's real option values) and, on "Select this size", drives
 * Prestige's own <variant-picker> so the real variant changes.
 *
 * How the hand-off to Prestige works (verified against assets/theme.js):
 *   - VariantPicker delegates 'change' on `input[data-option-position][form="<form-id>"]`.
 *   - It then reads the active combination from the form's `input[data-option-position]:checked`
 *     elements, sorted by data-option-position, mapping each to input.value.
 *   - input.value is the OPTION VALUE ID, not the option's text — snippets/option-value.liquid
 *     assigns `value = option_value.id`. So we match on the id the Liquid gave us, never on "M".
 * Setting `checked` and dispatching a bubbling 'change' is therefore exactly equivalent to the
 * customer clicking the swatch themselves. No cart or price logic is touched directly.
 */
(function () {
  'use strict';

  var WAIST_CAPTION = 'Measure where your pants actually sit, over a shirt if you tuck in.';
  var PANT_CAPTION = "Don't know your waist offhand? This gets you close.";
  var PANT_TO_WAIST_OFFSET = 2; // Distil's published rule: pants size ~= waist - 2"
  var STORAGE_KEY = 'distil_size_profile';

  function parseJSON(value, fallback) {
    if (!value) return fallback;
    try {
      var parsed = JSON.parse(value);
      return parsed == null ? fallback : parsed;
    } catch (e) {
      return fallback;
    }
  }

  class SizeRecommender extends HTMLElement {
    connectedCallback() {
      if (this._initialised) return;
      this._initialised = true;

      this.sizes = parseJSON(this.dataset.sizes, []).filter(function (size) {
        return typeof size.min === 'number' && typeof size.max === 'number';
      });
      this.formId = this.dataset.formId || null;
      this.optionPosition = this.dataset.optionPosition || null;
      this.modal = this.dataset.modalId ? document.getElementById(this.dataset.modalId) : null;

      this.state = { unit: 'in', fit: 'standard', source: null };

      this.cacheElements();
      this.bindEvents();
    }

    cacheElements() {
      this.steps = this.querySelectorAll('[data-src-step]');
      this.waistInput = this.querySelector('[data-src-waist-input]');
      this.pantInput = this.querySelector('[data-src-pant-input]');
      this.waistCaption = this.querySelector('[data-src-waist-caption]');
      this.pantCaption = this.querySelector('[data-src-pant-caption]');
      this.unitButtons = this.querySelectorAll('[data-src-unit]');
      this.unitSuffix = this.querySelector('[data-src-unit-suffix]');
      this.measureToggle = this.querySelector('[data-src-measure-toggle]');
      this.measureHelp = this.querySelector('[data-src-measure-help]');
      this.error = this.querySelector('[data-src-error]');
      this.fitButtons = this.querySelectorAll('[data-src-fit]');
      this.resultInRange = this.querySelector('[data-src-result-in-range]');
      this.resultOutOfRange = this.querySelector('[data-src-result-out-of-range]');
      this.resultBadge = this.querySelector('[data-src-result-badge]');
      this.resultReason = this.querySelector('[data-src-result-reason]');
      this.altBox = this.querySelector('[data-src-alt-box]');
      this.selectSizeButton = this.querySelector('[data-src-select-size]');
      this.outOfRangeMessage = this.querySelector('[data-src-out-of-range-msg]');
    }

    bindEvents() {
      var self = this;

      this.addEventListener('click', function (event) {
        var target = event.target.closest('[data-src-unit], [data-src-fit], [data-src-continue], [data-src-see-result], [data-src-back], [data-src-measure-toggle], [data-src-select-size]');
        if (!target || !self.contains(target)) return;

        if (target.hasAttribute('data-src-unit')) return self.setUnit(target.getAttribute('data-src-unit'));
        if (target.hasAttribute('data-src-fit')) return self.setFit(target.getAttribute('data-src-fit'));
        if (target.hasAttribute('data-src-continue')) return self.goToFitStep();
        if (target.hasAttribute('data-src-see-result')) return self.showResult();
        if (target.hasAttribute('data-src-back')) return self.goToStep(1);
        if (target.hasAttribute('data-src-measure-toggle')) return self.toggleMeasureHelp(target);
        if (target.hasAttribute('data-src-select-size')) return self.applySize(self._recommendedId);
      });

      if (this.waistInput) this.waistInput.addEventListener('input', function () { self.onWaistInput(); });
      if (this.pantInput) this.pantInput.addEventListener('input', function () { self.onPantInput(); });
    }

    /* ---------- steps ---------- */

    goToStep(step) {
      this.steps.forEach(function (element) {
        element.classList.toggle('is-active', Number(element.dataset.srcStep) === step);
      });
    }

    goToFitStep() {
      if (this.currentWaist() === null) {
        if (this.error) this.error.hidden = false;
        if (this.waistInput) this.waistInput.focus();
        return;
      }
      if (this.error) this.error.hidden = true;
      this.goToStep(2);
    }

    toggleMeasureHelp(button) {
      if (!this.measureHelp) return;
      var isOpen = !this.measureHelp.hidden;
      this.measureHelp.hidden = isOpen;
      button.setAttribute('aria-expanded', String(!isOpen));
    }

    /* ---------- inputs ---------- */

    setUnit(unit) {
      this.state.unit = unit;
      this.unitButtons.forEach(function (button) {
        button.classList.toggle('is-active', button.getAttribute('data-src-unit') === unit);
      });
      if (this.unitSuffix) this.unitSuffix.textContent = unit;
      this.onWaistInput();
    }

    setFit(fit) {
      this.state.fit = fit;
      this.fitButtons.forEach(function (button) {
        var active = button.getAttribute('data-src-fit') === fit;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    }

    waistFromWaistField() {
      if (!this.waistInput || this.waistInput.value === '') return null;
      var value = parseFloat(this.waistInput.value);
      if (isNaN(value)) return null;
      return this.state.unit === 'cm' ? value / 2.54 : value;
    }

    waistFromPantField() {
      if (!this.pantInput || this.pantInput.value === '') return null;
      var value = parseFloat(this.pantInput.value);
      if (isNaN(value)) return null;
      return value + PANT_TO_WAIST_OFFSET;
    }

    currentWaist() {
      if (this.state.source === 'pant') return this.waistFromPantField();
      if (this.state.source === 'waist') return this.waistFromWaistField();
      var waist = this.waistFromWaistField();
      return waist !== null ? waist : this.waistFromPantField();
    }

    onWaistInput() {
      this.state.source = 'waist';
      if (this.error) this.error.hidden = true;
      this.setCaption(this.waistCaption, WAIST_CAPTION, false);

      var waist = this.waistFromWaistField();
      if (waist === null) return this.setCaption(this.pantCaption, PANT_CAPTION, false);
      this.setCaption(this.pantCaption, 'Likely pants size ' + (Math.round(waist) - PANT_TO_WAIST_OFFSET), true);
    }

    onPantInput() {
      this.state.source = 'pant';
      if (this.error) this.error.hidden = true;
      this.setCaption(this.pantCaption, PANT_CAPTION, false);

      var waist = this.waistFromPantField();
      if (waist === null) return this.setCaption(this.waistCaption, WAIST_CAPTION, false);
      var display = this.state.unit === 'cm' ? Math.round(waist * 2.54) + ' cm' : waist + '"';
      this.setCaption(this.waistCaption, 'Likely measured waist ' + display, true);
    }

    setCaption(element, text, filled) {
      if (!element) return;
      element.textContent = text;
      element.classList.toggle('is-filled', !!filled);
    }

    /* ---------- recommendation ---------- */

    recommend(waist, fit) {
      if (!this.sizes.length) return { outOfRange: true, waist: Math.round(waist || 0) };

      var rounded = Math.round(waist);
      var first = this.sizes[0];
      var last = this.sizes[this.sizes.length - 1];
      if (rounded < first.min || rounded > last.max) return { outOfRange: true, waist: rounded };

      var index = -1;
      for (var i = 0; i < this.sizes.length; i++) {
        if (rounded >= this.sizes[i].min && rounded <= this.sizes[i].max) { index = i; break; }
      }
      // A gap between two configured bands (e.g. 29-30 with ranges 26-29 and 31-33): round up to
      // the next band rather than refusing to answer, which is the safer error for a belt.
      if (index === -1) {
        for (var j = 0; j < this.sizes.length; j++) {
          if (rounded < this.sizes[j].min) { index = j; break; }
        }
        if (index === -1) index = this.sizes.length - 1;
      }

      var band = this.sizes[index];
      var span = band.max - band.min;
      var position = span > 0 ? (rounded - band.min) / span : 0.5;
      // Only a waist sitting exactly on a band's top value counts as "between sizes" and rounds up
      // (e.g. 37" in a 34-37 M band -> L, with M offered as the alternate). 36" stays in M.
      var atTopOfBand = rounded >= band.max && index < this.sizes.length - 1;

      var finalIndex = index;
      if (fit === 'snug' && position <= 0.33 && index > 0) finalIndex = index - 1;
      if (fit === 'relaxed' && position >= 0.66 && index < this.sizes.length - 1) finalIndex = index + 1;

      var alternate = null;
      if (atTopOfBand && finalIndex <= index) {
        alternate = this.sizes[finalIndex];
        finalIndex = Math.min(finalIndex + 1, this.sizes.length - 1);
      }
      finalIndex = Math.max(0, Math.min(finalIndex, this.sizes.length - 1));

      return {
        outOfRange: false,
        waist: rounded,
        size: this.sizes[finalIndex],
        alternate: alternate && alternate.id !== this.sizes[finalIndex].id ? alternate : null,
        isBoundary: atTopOfBand,
        fit: fit
      };
    }

    showResult() {
      var result = this.recommend(this.currentWaist(), this.state.fit);
      this.goToStep(3);

      if (result.outOfRange) {
        if (this.resultInRange) this.resultInRange.hidden = true;
        if (this.resultOutOfRange) this.resultOutOfRange.hidden = false;
        if (this.outOfRangeMessage) {
          var low = this.sizes.length ? this.sizes[0].min : null;
          var high = this.sizes.length ? this.sizes[this.sizes.length - 1].max : null;
          this.outOfRangeMessage.textContent = this.sizes.length
            ? 'A ' + result.waist + '" waist sits outside this belt’s size range (' + low + '"–' + high + '"). Another MagLock material may cover a wider range — or contact us and we’ll help you find the right option.'
            : 'This product has no size ranges configured yet, so we can’t make a recommendation.';
        }
        this._recommendedId = null;
        return;
      }

      if (this.resultInRange) this.resultInRange.hidden = false;
      if (this.resultOutOfRange) this.resultOutOfRange.hidden = true;

      this._recommendedId = result.size.id;
      if (this.resultBadge) this.resultBadge.textContent = result.size.name;
      if (this.selectSizeButton) this.selectSizeButton.textContent = 'Select size ' + result.size.name;

      if (this.resultReason) {
        var fitLabel = result.fit.charAt(0).toUpperCase() + result.fit.slice(1);
        var reason = result.isBoundary
          ? 'You’re between sizes at ' + result.waist + '" — we rounded up to ' + result.size.name + ' so it’s never too tight to wear.'
          : 'Based on a ' + result.waist + '" waist, ' + fitLabel + ' fit.';
        if (this.state.source === 'pant') {
          reason += ' Estimated from your pants size — measure your waist directly for the most accurate size.';
        }
        this.resultReason.textContent = reason;
      }

      this.renderAlternate(result.alternate);
    }

    renderAlternate(alternate) {
      if (!this.altBox) return;
      this.altBox.textContent = '';
      if (!alternate) return;

      var self = this;
      var wrapper = document.createElement('div');
      wrapper.className = 'size-recommender__alt';

      var label = document.createElement('span');
      label.textContent = 'Prefer a closer fit? Try ' + alternate.name + '.';

      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'button button--outline';
      button.textContent = 'Use ' + alternate.name;
      button.addEventListener('click', function () { self.applySize(alternate.id); });

      wrapper.appendChild(label);
      wrapper.appendChild(button);
      this.altBox.appendChild(wrapper);
    }

    /* ---------- hand-off to Prestige's variant picker ---------- */

    findOptionInput(optionValueId) {
      if (!this.formId || optionValueId == null) return null;
      var selector = 'input[data-option-position][form="' + this.formId + '"]';
      if (this.optionPosition) {
        selector = 'input[data-option-position="' + this.optionPosition + '"][form="' + this.formId + '"]';
      }
      var inputs = document.querySelectorAll(selector);
      for (var i = 0; i < inputs.length; i++) {
        if (String(inputs[i].value) === String(optionValueId)) return inputs[i];
      }
      return null;
    }

    applySize(optionValueId) {
      var input = this.findOptionInput(optionValueId);

      if (input) {
        if (!input.checked) {
          input.checked = true;
          // Bubbles so VariantPicker's delegated 'change' listener picks it up — this is the
          // same event path a real click produces.
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify({
            id: optionValueId,
            waist: this.currentWaist(),
            fit: this.state.fit,
            ts: Date.now()
          }));
        } catch (e) {
          /* storage unavailable (private mode, blocked cookies) — non-fatal */
        }
      } else if (window.console) {
        console.warn(
          '[size-recommender] Could not find the variant input for option value ' + optionValueId +
          ' (form "' + this.formId + '", option position ' + this.optionPosition + '). ' +
          'The size was not applied.'
        );
      }

      this.closeModal();
    }

    closeModal() {
      if (this.modal && typeof this.modal.hide === 'function') {
        this.modal.hide();
      } else if (this.modal) {
        this.modal.dispatchEvent(new CustomEvent('dialog:force-close', { bubbles: true }));
      }
    }
  }

  if (!window.customElements.get('size-recommender')) {
    window.customElements.define('size-recommender', SizeRecommender);
  }
})();
